import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { EnvelopeIcon, ChevronDownIcon, PhoneIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import logoWhite from '../assests/logo-white.png';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

// Promotional sits right after Home, ahead of the product-browsing links.
const NAV_LINKS = [
  { to: '/', key: 'home' },
  { to: '/promotional', key: 'promotional' },
  { to: '/products', key: 'products' },
  { to: '/brands', key: 'brands' },
  { to: '/clients', key: 'clients' },
];

// Grouped under a "Discover" dropdown rather than sitting as top-level
// links — company background, hiring, and how to reach us are all
// "learn more about us" content rather than the core product-browsing flow.
const DISCOVER_LINKS = [
  { to: '/company-info', key: 'companyInfo' },
  { to: '/careers', key: 'careers' },
  { to: '/contact', key: 'contact' },
];

// Full-screen drawer (the MENU button): big English label + localized one.
const DRAWER_LINKS = [
  { to: '/', en: 'HOME', key: 'home' },
  { to: '/promotional', en: 'NEWS', key: 'promotional' },
  { to: '/products', en: 'PRODUCTS', key: 'products' },
  { to: '/brands', en: 'BRANDS', key: 'brands' },
  { to: '/clients', en: 'CLIENTS', key: 'clients' },
  { to: '/company-info', en: 'COMPANY', key: 'companyInfo' },
  { to: '/careers', en: 'CAREERS', key: 'careers' },
  { to: '/contact', en: 'CONTACT', key: 'contactUs' },
];

function LanguageToggle({ className = '' }) {
  const { language, setLanguage } = useLanguage();
  return (
    <div className={`flex items-center gap-1.5 text-xs ${className}`} role="group" aria-label="Language">
      <button onClick={() => setLanguage('en')}
        className={`transition-colors ${language === 'en' ? 'text-primary font-bold' : 'text-white/70 hover:text-white'}`}>
        EN
      </button>
      <span className="text-white/30">/</span>
      <button onClick={() => setLanguage('ja')}
        className={`transition-colors ${language === 'ja' ? 'text-primary font-bold' : 'text-white/70 hover:text-white'}`}>
        日本語
      </button>
    </div>
  );
}

const desktopLinkClass = (isActive) =>
  `relative py-2 text-[13px] font-medium tracking-wide text-white transition-colors hover:text-primary after:absolute after:left-0 after:-bottom-0.5 after:h-0.5 after:bg-primary after:transition-all ${isActive ? 'after:w-full' : 'after:w-0 hover:after:w-full'}`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const location = useLocation();
  const { t } = useLanguage();
  const discoverRef = useRef(null);

  const discoverActive = DISCOVER_LINKS.some(l => location.pathname.startsWith(l.to));

  useEffect(() => { setOpen(false); setDiscoverOpen(false); }, [location]);

  useEffect(() => {
    API.get('/settings').then(r => { if (r.data.phone) setPhone(r.data.phone); }).catch(() => {});
  }, []);

  // Lock page scroll behind the open drawer; Escape closes it.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open]);

  useEffect(() => {
    if (!discoverOpen) return;
    const onClickOutside = (e) => {
      if (discoverRef.current && !discoverRef.current.contains(e.target)) setDiscoverOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [discoverOpen]);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-charcoal h-16 lg:h-[84px] flex items-stretch justify-between gap-4 pl-4 lg:pl-8">
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0" aria-label="Sankyo Machine Tools — Home">
          <img src={logoWhite} alt="Sankyo Machine Tools" className="h-5 sm:h-6 lg:h-7 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center">
          <ul className="flex items-center gap-7">
            {NAV_LINKS.map(l => (
              <li key={l.to}>
                <NavLink to={l.to} end={l.to === '/'} className={({ isActive }) => desktopLinkClass(isActive)}>
                  {t(`nav.${l.key}`)}
                </NavLink>
              </li>
            ))}
            <li className="relative" ref={discoverRef}
              onMouseEnter={() => setDiscoverOpen(true)}
              onMouseLeave={() => setDiscoverOpen(false)}
            >
              <button onClick={() => setDiscoverOpen(o => !o)} aria-expanded={discoverOpen}
                className={`${desktopLinkClass(discoverActive)} inline-flex items-center gap-1`}>
                {t('nav.discover')}
                <ChevronDownIcon className={`w-3 h-3 shrink-0 transition-transform ${discoverOpen ? 'rotate-180' : ''}`} />
              </button>
              {discoverOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-5 z-50">
                  <div className="bg-charcoal border-t-2 border-primary py-2 min-w-[200px] shadow-xl">
                    {DISCOVER_LINKS.map(l => (
                      <NavLink key={l.to} to={l.to}
                        className={({ isActive }) =>
                          `block px-5 py-2.5 text-xs tracking-wider transition-colors ${isActive ? 'text-primary' : 'text-white hover:text-primary'}`
                        }>
                        {t(`nav.${l.key}`)}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </li>
          </ul>
        </nav>

        {/* Right cluster */}
        <div className="flex items-center">
          <LanguageToggle className="hidden sm:flex mr-5" />
          <Link to="/careers"
            className="hidden lg:flex items-center gap-3 mr-5 rounded-full bg-primary text-white pl-6 pr-4 py-3 text-xs tracking-[0.2em] hover:bg-white hover:text-primary transition-colors">
            {t('nav.recruit')}
            <UserGroupIcon className="w-4 h-4" />
          </Link>
          <Link to="/contact" aria-label={t('nav.inquiry')}
            className="h-16 w-16 lg:h-[84px] lg:w-[84px] bg-black text-white flex flex-col items-center justify-center gap-1 hover:bg-primary transition-colors">
            <EnvelopeIcon className="w-6 h-6 lg:w-7 lg:h-7" />
            <span className="text-[10px] lg:text-[11px] font-bold tracking-wide">{t('nav.inquiry')}</span>
          </Link>
          <button onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="site-drawer"
            className="h-16 w-16 lg:h-[84px] lg:w-[84px] bg-primary text-white flex flex-col items-center justify-center gap-1.5 hover:bg-[#2d89b4] transition-colors">
            <span className="relative block w-7 h-3">
              <span className={`absolute left-0 w-7 h-0.5 bg-white transition-all duration-300 ${open ? 'top-1.5 rotate-45' : 'top-0'}`} />
              <span className={`absolute left-0 w-7 h-0.5 bg-white transition-all duration-300 ${open ? 'top-1.5 -rotate-45' : 'top-3'}`} />
            </span>
            <span className="font-btn text-[10px] lg:text-[11px] font-semibold tracking-widest">{open ? t('nav.close') : t('nav.menu')}</span>
          </button>
        </div>
      </div>

      {/* Drawer */}
      <div id="site-drawer" aria-hidden={!open}
        className={`fixed inset-x-0 top-16 lg:top-[84px] bottom-0 bg-charcoal text-white overflow-y-auto transition-all duration-300 ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <div className="w-[90%] max-w-6xl mx-auto py-10 lg:py-16">
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10">
            {DRAWER_LINKS.map(l => (
              <li key={l.to} className="border-b border-white/15">
                <NavLink to={l.to} end={l.to === '/'}
                  className={({ isActive }) => `group flex items-center justify-between py-5 ${isActive ? 'text-primary' : 'text-white'}`}>
                  <span>
                    <span className="block font-display font-extrabold text-2xl tracking-tight group-hover:text-primary transition-colors">{l.en}</span>
                    <span className="block text-xs text-white/60 mt-1">{t(`common.${l.key}`)}</span>
                  </span>
                  <span className="w-2 h-2 border-t-2 border-r-2 border-current rotate-45 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Link to="/contact" className="btn-primary btn-arrow text-sm">{t('nav.contact')}</Link>
            <Link to="/careers" className="btn-dark btn-arrow text-sm border border-white/20">{t('nav.careers')}</Link>
            {phone && (
              <a href={`tel:${phone}`} className="flex items-center gap-2 text-sm text-white/80 hover:text-primary transition-colors">
                <PhoneIcon className="w-4 h-4" /> {phone}
              </a>
            )}
            <LanguageToggle className="sm:hidden" />
          </div>
        </div>
      </div>
    </header>
  );
}
